import { colors } from "./colors"

/**
 * Semantic Color Token 타입 정의
 * 모든 테마는 이 구조를 따라야 함
 */
type ColorTokens = {
  background: {
    page: string
    "surface-1": string
    "surface-2": string
    "surface-3": string
    "surface-4": string
    "alpha-grey-1": string
    "alpha-grey-2": string
    "alpha-grey-3": string
    "alpha-grey-4": string
    "alpha-grey-5": string
    "alpha-platinum-1": string
    "alpha-platinum-2": string
    "alpha-platinum-3": string
    "map-overlay": string
    "surface-platinum": string
    "surface-teal": string
    "teal-alpha-5": string
  }
  interactive: {
    "btn-fill-primary-bg-enabled": string
    "btn-fill-primary-bg-hover": string
    "btn-fill-primary-bg-pressed": string
    "btn-fill-primary-bg-disabled": string
    "btn-fill-text-enabled": string
    "btn-fill-text-disabled": string
    "btn-line-primary-border-enabled": string
    "btn-line-primary-border-disabled": string
    "btn-line-primary-text-enabled": string
    "btn-line-primary-text-disabled": string
    "btn-line-bg-enabled": string
    "btn-line-bg-hover": string
    "btn-line-bg-pressed": string
    "btn-line-bg-disabled": string
    "btn-line-default-border-enabled": string
    "btn-line-default-border-active": string
    "btn-line-default-border-disabled": string
    "btn-line-default-text-enabled": string
    "btn-line-default-text-disabled": string
    "btn-ghost-bg-hover": string
    "btn-ghost-bg-press": string
    "form-bg-default": string
    "form-bg-hover": string
    "form-bg-disabled": string
    "form-bg-checked-disabled": string
    "item-checked-default": string
    "item-checked-disabled": string
    "text-readonly": string
    "text-danger": string
    "text-success": string
    "text-warning": string
  }
  border: {
    "input-default": string
    "input-active": string
    "input-disabled": string
    "table-default": string
    "btn-checked": string
    "alpha-1": string
    "alpha-2": string
  }
  text: {
    "grey-primary": string
    "grey-default": string
    "grey-sub-1": string
    "grey-sub-2": string
  }
  icon: {
    enabled: string
    default: string
    readonly: string
    disabled: string
    primary: string
    "primary-active": string
  }
  field: {
    "ico-base": string
  }
  activity: {
    training: string
    league: string
    cup: string
    "friendly-match": string
    rehab: string
    etc: string
  }
}

/**
 * Light Mode (default)
 */
const lightTheme: ColorTokens = {
  background: {
    page: colors.platinum["200"],
    "surface-1": colors.neutral.white,
    "surface-2": colors.platinum["100"],
    "surface-3": colors.platinum["200"],
    "surface-4": colors.platinum["300"],
    "alpha-grey-1": colors.neutral.alpha["grey-004"],
    "alpha-grey-2": colors.neutral.alpha["grey-008"],
    "alpha-grey-3": colors.neutral.alpha["grey-012"],
    "alpha-grey-4": colors.neutral.alpha["grey-016"],
    "alpha-grey-5": colors.neutral.alpha["grey-020"],
    "alpha-platinum-1": colors.neutral.alpha["platinum-004"],
    "alpha-platinum-2": colors.neutral.alpha["platinum-008"],
    "alpha-platinum-3": colors.neutral.alpha["platinum-012"],
    "map-overlay": colors.neutral.alpha["grey-040"],
    "surface-platinum": colors.platinum["700"],
    "surface-teal": colors.primary["500"],
    "teal-alpha-5": colors.neutral.alpha["teal-020"],
  },
  interactive: {
    "btn-fill-primary-bg-enabled": colors.platinum["700"],
    "btn-fill-primary-bg-hover": colors.platinum["800"],
    "btn-fill-primary-bg-pressed": colors.platinum["900"],
    "btn-fill-primary-bg-disabled": colors.neutral.grey["300"],
    "btn-fill-text-enabled": colors.neutral.white,
    "btn-fill-text-disabled": colors.neutral.grey["500"],
    "btn-line-primary-border-enabled": colors.platinum["700"],
    "btn-line-primary-border-disabled": colors.neutral.grey["300"],
    "btn-line-primary-text-enabled": colors.platinum["700"],
    "btn-line-primary-text-disabled": colors.neutral.grey["400"],
    "btn-line-bg-enabled": colors.neutral.white,
    "btn-line-bg-hover": colors.neutral.alpha["platinum-004"],
    "btn-line-bg-pressed": colors.neutral.alpha["platinum-008"],
    "btn-line-bg-disabled": colors.neutral.grey["200"],
    "btn-line-default-border-enabled": colors.platinum["400"],
    "btn-line-default-border-active": colors.platinum["700"],
    "btn-line-default-border-disabled": colors.neutral.grey["300"],
    "btn-line-default-text-enabled": colors.platinum["600"],
    "btn-line-default-text-disabled": colors.neutral.grey["400"],
    "btn-ghost-bg-hover": colors.neutral.alpha["platinum-008"],
    "btn-ghost-bg-press": colors.neutral.alpha["platinum-012"],
    "form-bg-default": colors.neutral.grey["400"],
    "form-bg-hover": colors.neutral.alpha["platinum-004"],
    "form-bg-disabled": colors.neutral.grey["200"],
    "form-bg-checked-disabled": colors.neutral.grey["300"],
    "item-checked-default": colors.neutral.white,
    "item-checked-disabled": colors.neutral.grey["100"],
    "text-readonly": colors.neutral.grey["400"],
    "text-danger": colors.red["600"],
    "text-success": colors.green["600"],
    "text-warning": colors.yellow["500"],
  },
  border: {
    "input-default": colors.platinum["300"],
    "input-active": colors.platinum["700"],
    "input-disabled": colors.neutral.grey["300"],
    "table-default": colors.platinum["300"],
    "btn-checked": colors.platinum["300"],
    "alpha-1": colors.neutral.alpha["grey-004"],
    "alpha-2": colors.neutral.alpha["grey-008"],
  },
  text: {
    "grey-primary": colors.platinum["1000"],
    "grey-default": colors.platinum["700"],
    "grey-sub-1": colors.platinum["600"],
    "grey-sub-2": colors.platinum["500"],
  },
  icon: {
    enabled: colors.platinum["500"],
    default: colors.platinum["500"],
    readonly: colors.platinum["400"],
    disabled: colors.neutral.grey["400"],
    primary: colors.platinum["700"],
    "primary-active": colors.platinum["900"],
  },
  field: {
    "ico-base": colors.platinum["400"],
  },
  activity: {
    training: colors.sky["500"],
    league: colors.yellow["400"],
    cup: colors.brown["600"],
    "friendly-match": colors.green["600"],
    rehab: colors.purple["500"],
    etc: colors.platinum["500"],
  },
}

/**
 * Dark Mode (placeholder - Dark Mode.tokens.json 기반으로 채울 것)
 */
// const darkTheme: ColorTokens = { ... }

/**
 * 테마 레지스트리
 * 새 테마 추가 시 여기에 등록
 */
const themes = {
  light: lightTheme,
  // dark: darkTheme,
} as const

type ThemeName = keyof typeof themes

let currentTheme: ThemeName = "light"

export function setColorTheme(theme: ThemeName) {
  currentTheme = theme
}

export function getColorTheme(): ThemeName {
  return currentTheme
}

/**
 * 현재 활성 테마의 시맨틱 컬러 토큰
 *
 * 사용법:
 *   import { colorTokens } from "@/components/colorTokens"
 *   colorTokens.background.page
 *   colorTokens.text["grey-primary"]
 */
export const colorTokens: ColorTokens = new Proxy({} as ColorTokens, {
  get(_, category: string) {
    const theme = themes[currentTheme]
    const cat = theme[category as keyof ColorTokens]

    return cat
  },
})

export type { ColorTokens, ThemeName }
